

'use client';

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import type { CollectionItemDetails } from "@/types";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "@/lib/utils";
import { CalendarIcon, Loader2, MapPin, Paperclip, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "../ui/calendar";
import { ImageUploader } from "./image-uploader";
import { useState } from "react";
import Image from 'next/image';

const MAX_IMAGES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const formSchema = z.object({
    purchasePrice: z.coerce.number().min(0, "Price must be a non-negative number.").optional(),
    editionNumber: z.string().optional(),
    purchaseLocation: z.string().optional(),
    notes: z.string().optional(),
    purchaseDate: z.date().optional(),
    newPrivateImages: z.array(z.instanceof(File)).optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface EditCollectionDetailsFormProps {
    details: Partial<CollectionItemDetails> | null;
    onSave: (values: Partial<CollectionItemDetails> & { newPrivateImages?: File[], privateImageUrlsToDelete?: string[] }) => Promise<void>;
    onCancel: () => void;
    isSaving: boolean;
}

export function EditCollectionDetailsForm({ details, onSave, onCancel, isSaving }: EditCollectionDetailsFormProps) {
    const [privateImageUrlsToDelete, setPrivateImageUrlsToDelete] = useState<string[]>([]);
    
    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            purchasePrice: details?.purchasePriceMinorUnit !== undefined ? details.purchasePriceMinorUnit / 100 : undefined,
            editionNumber: details?.editionNumber || "",
            purchaseLocation: details?.location || "",
            notes: details?.notes || "",
            purchaseDate: details?.purchaseDate ? new Date(details.purchaseDate) : undefined,
            newPrivateImages: [],
        },
    });

    async function onSubmit(values: FormValues) {
        const payload = {
            purchasePriceMinorUnit: values.purchasePrice !== undefined ? Math.round(Number(values.purchasePrice) * 100) : undefined,
            purchasePriceCurrency: values.purchasePrice !== undefined ? 'USD' : undefined,
            editionNumber: values.editionNumber,
            location: values.purchaseLocation,
            notes: values.notes,
            purchaseDate: values.purchaseDate ? format(values.purchaseDate, 'yyyy-MM-dd') : undefined,
            newPrivateImages: values.newPrivateImages,
            privateImageUrlsToDelete: privateImageUrlsToDelete,
        };
        await onSave(payload);
    }
    
     const handleRemoveExistingImage = (url: string) => {
        setPrivateImageUrlsToDelete(prev => [...prev, url]);
    };
    
    const existingPrivateUrls = details?.privateImageUrls?.filter(url => !privateImageUrlsToDelete.includes(url)) || [];

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <FormField
                        control={form.control}
                        name="purchasePrice"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Price Paid (USD)</FormLabel>
                                <FormControl>
                                    <Input type="number" step="0.01" placeholder="e.g. 1000.00" {...field} value={field.value ?? ''} onChange={e => field.onChange(e.target.value === '' ? undefined : e.target.valueAsNumber)} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="editionNumber"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Edition Number</FormLabel>
                                <FormControl>
                                    <Input placeholder="e.g. 25/100 or AP" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                 <FormField
                    control={form.control}
                    name="purchaseDate"
                    render={({ field }) => (
                        <FormItem className="flex flex-col">
                        <FormLabel>Purchase Date</FormLabel>
                        <Popover>
                            <PopoverTrigger asChild>
                            <FormControl>
                                <Button
                                variant={"outline"}
                                className={cn(
                                    "w-full pl-3 text-left font-normal",
                                    !field.value && "text-muted-foreground"
                                )}
                                >
                                {field.value ? (
                                    format(field.value, "PPP")
                                ) : (
                                    <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                            </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(date) =>
                                date > new Date() || date < new Date("1900-01-01")
                                }
                                initialFocus
                            />
                            </PopoverContent>
                        </Popover>
                        <FormMessage />
                        </FormItem>
                    )}
                />
                 <FormField
                    control={form.control}
                    name="purchaseLocation"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="flex items-center gap-2"><MapPin className="h-4 w-4"/> Purchase Location</FormLabel>
                            <FormControl>
                                <Input placeholder="e.g. Subliminal Projects, Gagosian, Artist..." {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Notes</FormLabel>
                            <FormControl>
                                <Textarea placeholder="Any private notes about this artwork..." {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="newPrivateImages"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="flex items-center gap-2"><Paperclip className="h-4 w-4"/> Private Attachments</FormLabel>
                            <FormDescription>
                                Upload receipts, certificates of authenticity, or other private documents.
                            </FormDescription>
                            <FormControl>
                                <ImageUploader
                                    value={field.value || []}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    maxFiles={MAX_IMAGES}
                                    maxSize={MAX_FILE_SIZE}
                                    currentImageUrls={existingPrivateUrls}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                 {existingPrivateUrls.length > 0 && (
                    <div>
                        <FormLabel>Current Attachments</FormLabel>
                        <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                            {existingPrivateUrls.map(url => (
                                <div key={url} className="relative group">
                                    <a href={url} target="_blank" rel="noopener noreferrer">
                                        <div className="aspect-square relative rounded-md overflow-hidden border">
                                            <Image src={url} alt="Private attachment" fill sizes="100px" className="object-cover"/>
                                        </div>
                                    </a>
                                     <Button
                                        type="button"
                                        variant="destructive"
                                        size="icon"
                                        className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100"
                                        onClick={() => handleRemoveExistingImage(url)}
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={onCancel} disabled={isSaving}>Cancel</Button>
                    <Button type="submit" disabled={(!form.formState.isDirty && privateImageUrlsToDelete.length === 0) || isSaving}>
                         {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {isSaving ? "Saving..." : "Save Details"}
                    </Button>
                </div>
            </form>
        </Form>
    )
}
